// Wraps arbitrary body HTML in a standalone, print-ready HTML document that
// reuses the app's own compiled stylesheets. Native only — no PDF/canvas/
// html-to-image libraries. The resulting string is rendered into an <iframe>
// for a WYSIWYG preview and printed via the browser's built-in "Save as PDF"
// (window.print). Because preview and download use the SAME document, they match.
//
// Reusable for any "print this piece of the live DOM" need: capture a node's
// innerHTML, pass it here, drop the result into an iframe's `srcdoc`. An optional
// `coverHtml` (see buildPdfCover) is rendered as a full-bleed first page.

// Copy every <style> and <link rel="stylesheet"> from the current document head.
// In dev (CRA/style-loader) styles are inline <style> tags; in a production build
// they are <link> tags. `link.href` (the property) resolves to an absolute URL,
// which is required because relative hrefs break under the iframe's about:srcdoc.
const collectHeadStyles = () => {
    const nodes = document.querySelectorAll('style, link[rel="stylesheet"]');
    let out = '';
    nodes.forEach((node) => {
        if (node.tagName === 'LINK') {
            out += `<link rel="stylesheet" href="${node.href}">`;
        } else {
            out += node.outerHTML;
        }
    });
    return out;
};

const two = (n) => String(n).padStart(2, '0');
// Current date, DD MM YYYY (e.g. "11 08 2026").
const formatToday = () => {
    const d = new Date();
    return `${two(d.getDate())} ${two(d.getMonth() + 1)} ${d.getFullYear()}`;
};

// Builds the branded PDF cover page markup. Uses <div>s + the `report-cover`
// class prefix — deliberately NOT `pdf-*`/`cover-page`, which the app already
// defines (RenderPdf) and collectHeadStyles copies into this iframe; a unique
// prefix guarantees none of that CSS can style (or mis-lay-out) the cover.
// Content is caller-supplied; only pass trusted, app-generated strings.
export const buildPdfCover = ({
    title = 'Summary Report',
    subtitle = '',
    reportName = '',
    confidential = true,
    preparedOn,
    includesText = '',
} = {}) => {
    const date = preparedOn || formatToday();
    return `
    <section class="report-cover">
      <div class="report-cover__band">
        <div class="report-cover__title">${title}</div>
        ${subtitle ? `<div class="report-cover__subtitle">${subtitle}</div>` : ''}
      </div>
      <div class="report-cover__info">
        ${reportName ? `<div class="report-cover__report">${reportName}</div>` : ''}
        ${confidential ? `<div class="report-cover__confidential">Confidential</div>` : ''}
        <div class="report-cover__meta">Report Prepared: ${date}</div>
        ${includesText ? `
          <div class="report-cover__includes-label">This report includes:</div>
          <div class="report-cover__includes">${includesText}</div>
        ` : ''}
      </div>
    </section>`;
};

export const buildPrintDocument = (bodyHtml, options = {}) => {
    const { title = 'Export', contentWidth, coverHtml = '' } = options;
    // On-screen preview mimics the live layout width so it matches the app;
    // print resets to auto so content fits the sheet the browser is given.
    const widthCss = contentWidth ? `width:${contentWidth}px;` : '';

    return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8" />
<title>${title}</title>
${collectHeadStyles()}
<style>
  html, body { margin: 0; padding: 0; background: #f0f2f5; }
  .pdf-export-root {
    ${widthCss}
    margin: 24px auto;
    box-sizing: border-box;
    background: #ffffff;
    box-shadow: 0 1px 4px rgba(16, 24, 40, 0.12);
  }
  .pdf-body { padding: 24px; }
  /* Rasterized wide elements (e.g. the heatmap) scale to the page and stay whole. */
  .pdf-body img { max-width: 100%; height: auto; break-inside: avoid; page-break-inside: avoid; }

  /* ---- Cover page ----
     Every rule sets display/width/float/position defensively: the app's copied
     stylesheets can carry bare-tag or reset rules, and this guarantees a
     full-width, vertically-stacked cover no matter what leaks in. */
  .report-cover {
    display: block; width: 100%; box-sizing: border-box;
    float: none; position: static;
    page-break-after: always; break-after: page;
  }
  .report-cover__band {
    display: block; width: 100%; box-sizing: border-box; float: none;
    background: #35807e;
    color: #ffffff;
    padding: 72px 56px;
  }
  .report-cover__title { display: block; margin: 0; font-size: 52px; font-weight: 800; line-height: 1.05; color: #ffffff; }
  .report-cover__subtitle { display: block; margin: 16px 0 0; font-size: 23px; font-weight: 400; color: #ffffff; }
  .report-cover__info { display: block; width: 100%; box-sizing: border-box; float: none; clear: both; padding: 48px 56px; color: #29363d; }
  .report-cover__report { display: block; margin: 0 0 12px; font-size: 26px; font-weight: 700; color: #29363d; }
  .report-cover__confidential { display: block; margin: 0 0 4px; font-size: 13px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: #29363d; }
  .report-cover__meta { display: block; margin: 0 0 36px; font-size: 15px; color: #29363d; }
  .report-cover__includes-label { display: block; margin: 0 0 6px; font-size: 15px; font-weight: 700; color: #29363d; }
  .report-cover__includes { display: block; margin: 0; max-width: 620px; font-size: 15px; line-height: 1.5; color: #29363d; }

  /* Force background colours to print (teal cover band, shaded heatmap cells, badges, chart fills). */
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }

  /* Portrait A4 throughout — cover and content pages share one orientation. */
  @page { size: A4 portrait; margin: 10mm; }

  @media print {
    html, body { background: #ffffff; }
    .pdf-export-root { width: 1095px; margin: 0; box-shadow: none; }
    .pdf-body { padding: 0; } /* @page margin handles spacing on content pages */

    /* Pagination: keep each component whole. Every section's body is a
       .cardwrapper (SummaryCards is the one exception, .summary-cards), so
       when a card can't fit the space left on the page it moves to the next page
       instead of splitting across the break. Its section header stays glued to it
       via break-after: avoid. A card taller than a full page can't be kept whole,
       so the atomic rules below let *that* card break only at sensible internal
       boundaries (rows, cards, comments) rather than mid-element. */
    .section-header { break-inside: avoid; page-break-inside: avoid; break-after: avoid; page-break-after: avoid; }
    .cardwrapper, .summary-cards { break-inside: avoid; page-break-inside: avoid; }
    .cardwrapper > h3,
    .cardwrapper > .description-container { break-after: avoid; page-break-after: avoid; }
    tr,
    .summary-card, .raters-card, .ktcard,
    .ratings-legend__item,
    .lt-align__item,
    .divergence__row,
    .samc__comment, .samc__gist {
      break-inside: avoid; page-break-inside: avoid;
    }
  }
</style>
</head>
<body>
  <div class="pdf-export-root">
    ${coverHtml}
    <div class="pdf-body">${bodyHtml}</div>
  </div>
</body>
</html>`;
};
